import { useEffect, useState } from 'react';
import { EDGE_ID, store } from '../data/store';
import type { TapeSummary } from '../types';
import { cleanTape } from '../../scripts/tape-clean.mjs';
import { cellBasis, tapeSummary } from '../../scripts/tape-core.mjs';
import { dayFlags, isAllIn, pointAllIn as basisOf, pointBasisKnown } from '../../shared/providers.mjs';
import priceTrust from '../../scripts/price-trust.json';

/**
 * Our own daily price recordings for one event, as /history/<id>.json stores
 * them: { marketplace: [[date, floor], …] }. One fetch per event per page
 * life — the price board, the ticker and the trust facts all read the same
 * promise, so opening an event costs one request however many consumers it
 * has. Nothing here is estimated: every number is a floor we observed.
 *
 * A point may carry its fee basis as a third slot (1 = fees included, 0 = before fees), written
 * only when it differs from its marketplace's rule (scripts/gen-catalog.mjs): a Ticket Squeeze
 * widget reading is fees included, its feed price is not. pointAllIn() reads it. A fourth slot, 2,
 * says the price is for two or more tickets (the widget readings from 2026-10-22).
 */
export type TapePoint = [date: string, price: number, allIn?: 0 | 1, qty?: 2];
export type Tape = Record<string, TapePoint[]>;

const cache = new Map<string, Promise<Tape | null>>();

export const loadTape = (eventId: string): Promise<Tape | null> => {
  let p = cache.get(eventId);
  if (!p) {
    p = fetch(`/history/${eventId}.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d): Tape | null => {
        if (!d) return null;
        // the oldest tapes are a bare HelloTickets series
        if (Array.isArray(d)) return d.length ? { HelloTickets: d as TapePoint[] } : null;
        const t = d as Tape;
        return Object.values(t).some((pts) => Array.isArray(pts) && pts.length) ? t : null;
      })
      .catch(() => null);
    cache.set(eventId, p);
  }
  return p;
};

/** The tape for an event, null until it arrives (and null when there is none yet). */
export const useTape = (eventId: string | undefined) => useTapeStatus(eventId).tape;

/**
 * Ids whose recording file exists only when the row says it has a tape: the store's edge ids (EDGE_ID: tn-, ht-)
 * and tm- ids, which have no recording either (live 2026-10-30: /history/tm-vvG1zZ_4UFuPuT.json was a 404 on every
 * open). A local test, not a change of EDGE_ID: that one also decides where a row is loaded from.
 */
const TAPE_ON_REQUEST = /^(tn|ht|tm)-/;

/**
 * Whether /history/<id>.json is worth asking for. Edge events (Ticket Network and HelloTickets
 * long-tail ids, which only the edge API carries) and tm- events have a recording file only when
 * their row says it has a tape; otherwise the file 404s every time. 'wait' = the row has not fully arrived yet.
 */
export const tapeWanted = (eventId: string | undefined): 'ask' | 'skip' | 'wait' => {
  if (!eventId || !(EDGE_ID.test(eventId) || TAPE_ON_REQUEST.test(eventId))) return 'ask';
  const row = store.get(eventId);
  if (!row || row.partial) return 'wait';
  return row.tape?.rows?.length ? 'ask' : 'skip';
};

/** The tape and whether its fetch has finished (`done`): null + done means there is none. */
export const useTapeStatus = (eventId: string | undefined) => {
  const [state, setState] = useState<{ id: string | undefined; tape: Tape | null; done: boolean }>({ id: eventId, tape: null, done: false });
  const want = tapeWanted(eventId);
  useEffect(() => {
    setState({ id: eventId, tape: null, done: false });
    if (!eventId || import.meta.env.SSR || want === 'wait') return;
    if (want === 'skip') {
      setState({ id: eventId, tape: null, done: true });
      return;
    }
    let alive = true;
    loadTape(eventId).then((t) => {
      if (alive) setState({ id: eventId, tape: t, done: true });
    });
    return () => {
      alive = false;
    };
  }, [eventId, want]);
  // a stale answer for the previous event never shows on the next one
  return state.id === eventId ? state : { id: eventId, tape: null, done: false };
};

/** A point's fee basis: its own third slot when the build wrote one, else (Ticket Squeeze before it
 *  recorded one) the shape of the number, else its marketplace's rule (shared/providers.mjs pointAllIn). */
export const pointAllIn = (p: TapePoint, source: string) => basisOf(p, source);

export type ChartPoint = { date: string; price: number; allIn: boolean };
export type ChartSeries = { name: string; points: ChartPoint[] };

/**
 * What the price chart draws: the recorded prices exactly as the history table shows them, after
 * the same cleaning as the summary (trusted readings only, junk readings out: scripts/tape-clean.mjs)
 * and without the day's phantom floors — a before-fees reading under half that day's cheapest
 * fees-included one (shared/providers.mjs markSuspects, the board's own rule). `left` counts those.
 */
export const chartSeries = (tape: Tape | null, standIns?: ReadonlySet<string>): { series: ChartSeries[]; left: number } => {
  if (!tape) return { series: [], left: 0 };
  // tape-clean.d.mts types points as pairs; it passes every slot through untouched
  const kept = cleanTape(tape as unknown as Record<string, [string, number][]>, trusted).clean as unknown as Tape;
  const byDay = new Map<string, { source: string; allIn: boolean; known: boolean; price: number; standIn: boolean }[]>();
  for (const [source, pts] of Object.entries(kept)) {
    for (const p of pts ?? []) {
      if (!(p[1] > 0) || !trusted(source, p[0])) continue;
      const day = byDay.get(p[0]) ?? [];
      day.push({ source, allIn: pointAllIn(p, source), known: pointBasisKnown(p, source), price: p[1], standIn: !!standIns?.has(`${source}|${Math.round(p[1] * 100)}`) });
      byDay.set(p[0], day);
    }
  }
  const out = new Map<string, ChartPoint[]>();
  let left = 0;
  for (const [date, offers] of byDay) {
    // the day's phantoms, by the one rule every statistic uses (shared/providers.mjs dayFlags)
    const flags = dayFlags(offers);
    offers.forEach((o, i) => {
      if (flags[i]) {
        left++;
        return;
      }
      const list = out.get(o.source) ?? [];
      list.push({ date, price: o.price, allIn: o.allIn });
      out.set(o.source, list);
    });
  }
  const series = [...out.entries()]
    .map(([name, points]) => ({ name, points: points.sort((a, b) => (a.date < b.date ? -1 : 1)) }))
    // cheapest today first, like the board (colours stay fixed per marketplace)
    .sort((a, b) => a.points[a.points.length - 1].price - b.points[b.points.length - 1].price || a.name.localeCompare(b.name));
  return { series, left };
};

/**
 * The last 30 days of each marketplace's series, rebuilt from the summary the
 * event row carries (scripts/tape-summary.mjs). The board renders from this on
 * the server and in the browser's first paint, so crawlers see the real
 * history instead of "tracking starts today"; the full tape replaces it once
 * /history/<id>.json arrives.
 */
/** `dropUnconfirmed`: leave out the cells the server marked unconfirmed (phantoms and stand-in prices), for a
 *  summary recomputed from them: they would otherwise count again. */
export const tapeFromSummary = (s: TapeSummary | undefined, dropUnconfirmed = false): Tape | null => {
  if (!s?.rows?.length) return null;
  const out: Tape = {};
  s.cols.forEach((col, j) => {
    const pts: TapePoint[] = [];
    for (let i = s.rows.length - 1; i >= 0; i--) {
      const v = s.rows[i][j + 1];
      if (typeof v !== 'number' || !(v > 0)) continue;
      if (dropUnconfirmed && cellBasis(s.marks?.[i * s.cols.length + j], col).flagged) continue;
      // the cell's fee basis rides along (a third slot only where it differs from the marketplace's rule)
      const allIn = cellBasis(s.marks?.[i * s.cols.length + j], col).allIn;
      pts.push(allIn === isAllIn({ source: col }) ? [String(s.rows[i][0]), v] : [String(s.rows[i][0]), v, allIn ? 1 : 0]);
    }
    if (pts.length) out[col] = pts;
  });
  return Object.keys(out).length ? out : null;
};

// ---- one storefront per visitor ------------------------------------------------------------

/**
 * Marketplaces we record at their US storefront price: HelloTickets (the build asks it with
 * Accept-Language en-US — the all-in price US buyers pay). Visitors outside the US are quoted
 * its international storefront, about a third more (functions/api/prices.ts), so for them the
 * recorded history leaves it out instead of showing a price they cannot get.
 */
export const US_STOREFRONT_ONLY: readonly string[] = ['HelloTickets'];

/** The tape without the given marketplaces (null when nothing is left). */
export const tapeWithout = (tape: Tape | null, drop: readonly string[] | undefined): Tape | null => {
  if (!tape || !drop?.length) return tape;
  const kept = Object.entries(tape).filter(([name, pts]) => !drop.includes(name) && Array.isArray(pts) && pts.length);
  return kept.length ? Object.fromEntries(kept) : null;
};

const TRUST = (priceTrust as { sources?: Record<string, { trustedFrom?: string }> }).sources ?? {};
/** The server's own publishing rule (scripts/price-trust.json, functions/_lib/edge-page.ts). */
const trusted = (source: string, date: string) => {
  const from = TRUST[source]?.trustedFrom;
  return !from || date >= from;
};

/**
 * The event's price summary as this visitor should see it. In the US: the server's, unchanged.
 * Elsewhere: recomputed without US_STOREFRONT_ONLY with the server's own rules
 * (scripts/tape-clean.mjs, scripts/tape-core.mjs) — from the full tape once it has loaded, from
 * the summary's last 30 days until then (keeping the server's tracking span). Undefined when
 * nothing else was recorded.
 */
export const summaryFor = (
  summary: TapeSummary | undefined,
  full: Tape | null,
  intl: boolean,
  today: string,
  /** the event's stand-in prices (shared/providers.mjs standInKeys(event.offers)), for the full tape */
  standIns?: ReadonlySet<string>
): TapeSummary | undefined => {
  if (!intl || !summary?.cols?.some((c) => US_STOREFRONT_ONLY.includes(c))) return summary;
  const kept = tapeWithout(full ?? tapeFromSummary(summary, true), US_STOREFRONT_ONLY);
  if (!kept) return undefined;
  // the d.mts files type points as pairs; a third (basis) slot passes through untouched
  const s = tapeSummary(cleanTape(kept as unknown as Record<string, [string, number][]>, trusted).clean, today, trusted, standIns ? { standIns: new Set(standIns) } : {}) as TapeSummary | null;
  if (!s) return undefined;
  return full ? s : { ...s, since: summary.since, days: summary.days, full30: summary.full30 };
};
